import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  ModalProps,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { DocumentType } from '../../../../../enums';
import { UploadDocumentDTO } from '../../../../../api';
import FileUpload from '../../../../../components/inputFile/inputFile';

const allowedDocuments = {
  [DocumentType.REGISTRY_EXTRACT]: DocumentType.REGISTRY_EXTRACT,
  [DocumentType.SHAREHOLDER_EXTRACT]: DocumentType.SHAREHOLDER_EXTRACT,
  [DocumentType.FINANCIAL_STATEMENT]: DocumentType.FINANCIAL_STATEMENT,
  [DocumentType.SOURCE_OF_FUNDS]: DocumentType.SOURCE_OF_FUNDS,
  [DocumentType.OWNERSHIP_STRUCTURE]: DocumentType.OWNERSHIP_STRUCTURE,
  [DocumentType.POWER_OF_ATTORNEY]: DocumentType.POWER_OF_ATTORNEY,
  [DocumentType.LICENCE]: DocumentType.LICENCE,
  [DocumentType.ARTICLES_OF_ASSOCIATION]: DocumentType.ARTICLES_OF_ASSOCIATION,
  [DocumentType.PARTNERSHIP_AGREEMENT]: DocumentType.PARTNERSHIP_AGREEMENT,
  [DocumentType.CHARTER_BY_LAWS]: DocumentType.CHARTER_BY_LAWS,
  [DocumentType.CERTIFICATE_OF_INCORPORATION]: DocumentType.CERTIFICATE_OF_INCORPORATION,
  [DocumentType.MEMORANDUM_OF_ASSOCIATION]: DocumentType.MEMORANDUM_OF_ASSOCIATION,
  [DocumentType.ACT_OF_FORMATION]: DocumentType.ACT_OF_FORMATION,
  [DocumentType.BUSINESS_REGISTRATION_CERTIFICATE]: DocumentType.BUSINESS_REGISTRATION_CERTIFICATE,
  [DocumentType.PROOF_OF_ADDRESS]: DocumentType.PROOF_OF_ADDRESS,
  [DocumentType.PROOF_OF_BANK_ACCOUNT]: DocumentType.PROOF_OF_BANK_ACCOUNT,
  [DocumentType.INTERNAL_POLICIES_ML_TF]: DocumentType.INTERNAL_POLICIES_ML_TF,
  [DocumentType.INTERNAL_POLICIES_SANCTION_IMPLEMENTATION]:
    DocumentType.INTERNAL_POLICIES_SANCTION_IMPLEMENTATION,
  [DocumentType.PROOF_OF_DIRECTOR_APPOINTMENT]: DocumentType.PROOF_OF_DIRECTOR_APPOINTMENT,
  [DocumentType.RESIDENCE_PERMIT]: DocumentType.RESIDENCE_PERMIT,
  [DocumentType.OTHER]: DocumentType.OTHER,
};

type UploadDocumentModalProps = {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  fields: UploadDocumentDTO['body'] | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    isFile?: boolean
  ) => void;
  isLoading: boolean;
};

export const UploadDocumentModal: FC<UploadDocumentModalProps> = (props) => {
  const { open, onClose, onUpload, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // validation for required fields
  const requiredFields = ['type', 'file', 'mode'] as (keyof UploadDocumentDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      // @ts-ignore
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  const documentTypeSelectOptions = Object.values(allowedDocuments).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.document-type.${it}`)}
      </MenuItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('profile-documents.modals.upload.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="type"
            value={fields?.type ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-documents.modals.upload.fields.document-type')}
            fullWidth
            // InputLabelProps={{ shrink: true }}
          >
            {documentTypeSelectOptions}
          </TextField>

          {/* <TextField
            type="file"
            name="file"
            onChange={(e) => fieldsHandler(e, true)}
            label={t('profile-documents.modals.upload.fields.upload-file')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          /> */}

          <FileUpload
            name="file"
            fieldsHandler={fieldsHandler}
            label={t('profile-documents.modals.upload.fields.upload-file')}
          />

          <FormControl>
            <FormLabel>{t('profile-documents.modals.upload.fields.hidden')}</FormLabel>

            <RadioGroup
              row
              name="mode"
              value={fields?.mode ?? ''}
              onChange={(e) => fieldsHandler(e)}
            >
              <FormControlLabel
                value="hidden"
                control={<Radio />}
                label={t('profile-documents.modals.upload.fields.hidden-yes')}
              />
              <FormControlLabel
                value="visible"
                control={<Radio />}
                label={t('profile-documents.modals.upload.fields.hidden-no')}
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onUpload()}
          disabled={isLoading || checkSubmitDisabling()}
        >
          {t('profile-documents.modals.upload.upload-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()} disabled={isLoading}>
          {t('profile-documents.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
